import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const HERO = "https://images.pexels.com/photos/23319054/pexels-photo-23319054.jpeg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to Aspen Executive Limo.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#1a1a1d] border-white/15 text-white focus-visible:ring-[#D4AF37] rounded-sm h-12";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex justify-center mb-10"><Logo imgClass="h-12 w-12" textClass="text-3xl" /></Link>
          <p className="text-xs tracking-label uppercase gold-text mb-3">Join Aspen Executive Limo</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Create account</h1>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Full Name</Label>
              <Input data-testid="register-name" required className={inputCls} placeholder="James Whitmore"
                value={form.name} onChange={(e) => update("name", e.target.value)} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Email</Label>
              <Input data-testid="register-email" type="email" required className={inputCls} placeholder="you@example.com"
                value={form.email} onChange={(e) => update("email", e.target.value)} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Phone</Label>
              <Input data-testid="register-phone" className={inputCls} placeholder="+1 555 000 0000"
                value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs tracking-wide mb-1.5 block">Password</Label>
              <Input data-testid="register-password" type={showPassword ? "text" : "password"} required minLength={6} className={inputCls} placeholder="At least 6 characters"
                value={form.password} onChange={(e) => update("password", e.target.value)} />
              <label className="flex items-center gap-2 mt-2 w-fit cursor-pointer select-none">
                <input type="checkbox" data-testid="register-show-password" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} className="h-4 w-4 accent-[#D4AF37] cursor-pointer" />
                <span className="text-zinc-400 text-sm">Show password</span>
              </label>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="flex items-center justify-center w-fit bg-[#D4AF37] text-black hover:bg-[#E0BC4A] disabled:opacity-50 transition-all duration-300 rounded-sm px-[3px] py-[1px] font-medium tracking-wide"
            >
              {loading ? "Creating…" : "Create Account"}
            </button>
          </form>

          <p className="text-zinc-400 text-sm mt-8 text-center font-light">
            Already a member?{" "}
            <Link to="/login" data-testid="goto-login" className="gold-text hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={HERO} alt="Luxury SUV" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30" />
        <div className="absolute bottom-16 left-12 right-12 text-right">
          <Link to="/" className="inline-flex justify-end"><Logo imgClass="h-12 w-12" textClass="text-3xl" /></Link>
          <h2 className="font-heading text-4xl font-light mt-6 leading-tight">Travel, reimagined.</h2>
          <p className="text-zinc-300 font-light mt-3 ml-auto max-w-sm">Join thousands who choose to arrive in style.</p>
        </div>
      </div>
    </div>
  );
}
